import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import LiquidationMapChart, { formatPrice } from '../components/LiquidationMapChart/LiquidationMapChart';
import {
  useHyperliquidLiqMap,
  useSyntheticLiqMap,
  useCombinedLiqMap,
  prepareChartSeries,
} from '../hooks/useLiquidationMap';
import { capture } from '../utils/analytics';
import './LiquidationMapPage.css';

const SYMBOLS = ['BTC', 'ETH', 'SOL', 'HYPE', 'kBONK', 'kPEPE'];
const STORAGE_KEY = 'tradermap.liqmap.symbol';

function readStoredSymbol() {
  try {
    if (typeof localStorage === 'undefined') return 'BTC';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SYMBOLS.includes(stored)) return stored;
  } catch {
    // ignore — Safari private mode etc.
  }
  return 'BTC';
}

function ChartSection({ titleKey, titleFallback, subtitleKey, subtitleFallback, symbol, hookResult, testId, variant }) {
  const { t } = useTranslation();
  const { data, loading, error } = hookResult;
  return (
    <section className="liqmap-page__section" data-testid={testId}>
      <div className="liqmap-page__section-head">
        <h2 className="liqmap-page__section-title">
          {t(titleKey, titleFallback)}
        </h2>
        <p className="liqmap-page__section-subtitle">
          {t(subtitleKey, subtitleFallback)}
        </p>
      </div>
      {error && (
        <div className="liqmap-page__error" role="alert" data-testid={`${testId}-error`}>
          {t('liqMap.error', 'Failed to load liquidation map.')} — {error}
        </div>
      )}
      <div className="liqmap-page__chart-wrap">
        <LiquidationMapChart symbol={symbol} data={data} loading={loading} variant={variant} />
      </div>
    </section>
  );
}

function LiquidationMapPage() {
  const { t } = useTranslation();
  const [symbol, setSymbol] = useState(readStoredSymbol);

  // Three independent hooks — each polls its own endpoint with visibility-pause.
  // Loading/error on one source must not block the other charts.
  const hlResult = useHyperliquidLiqMap({ symbol });
  const synthResult = useSyntheticLiqMap({ symbol });
  const combinedResult = useCombinedLiqMap({ symbol });

  // Pick a current price to display in the header — prefer combined (covers
  // all sources), fall back to whichever source has it.
  const headerPrice = useMemo(() => {
    const p =
      combinedResult.data?.currentPrice ??
      hlResult.data?.currentPrice ??
      synthResult.data?.currentPrice;
    return typeof p === 'number' ? p : null;
  }, [combinedResult.data, hlResult.data, synthResult.data]);

  // Persist symbol selection across reloads.
  useEffect(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, symbol);
      }
    } catch {
      // ignore
    }
  }, [symbol]);

  // Fire once on mount.
  useEffect(() => {
    capture('liqmap.viewed', { symbol });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface error events to PostHog. Each source is reported independently
  // so per-source health can be tracked.
  useEffect(() => {
    if (hlResult.error) {
      capture('liqmap.error_shown', { symbol, error: hlResult.error, source: 'hyperliquid' });
    }
  }, [hlResult.error, symbol]);
  useEffect(() => {
    if (synthResult.error) {
      capture('liqmap.error_shown', { symbol, error: synthResult.error, source: 'synthetic' });
    }
  }, [synthResult.error, symbol]);
  useEffect(() => {
    if (combinedResult.error) {
      capture('liqmap.error_shown', { symbol, error: combinedResult.error, source: 'combined' });
    }
  }, [combinedResult.error, symbol]);

  const handleSymbolChange = useCallback((nextSymbol) => {
    if (!SYMBOLS.includes(nextSymbol)) return;
    if (nextSymbol === symbol) return;
    capture('liqmap.symbol_changed', { from: symbol, to: nextSymbol });
    setSymbol(nextSymbol);
  }, [symbol]);

  return (
    <div className="liqmap-page">
      <SEO page="liquidationMap" />
      <Header />

      <div className="liqmap-page__content">
        <h1 className="liqmap-page__title">
          {t('liqMap.title', 'Liquidation Map')}
        </h1>
        <p className="liqmap-page__subtitle">
          {t('liqMap.subtitle', 'Where current positions would liquidate, by price bucket.')}
        </p>

        <div className="liqmap-page__controls">
          <label className="liqmap-page__symbol-label" htmlFor="liqmap-symbol-select">
            {t('liqMap.symbol_label', 'Symbol')}
          </label>
          <select
            id="liqmap-symbol-select"
            className="liqmap-page__symbol-select"
            value={symbol}
            onChange={(e) => handleSymbolChange(e.target.value)}
            data-testid="liqmap-symbol-select"
          >
            {SYMBOLS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {headerPrice != null && (
            <span className="liqmap-page__price">
              {t('liqMap.current_price', 'Mark')}: {formatPrice(headerPrice)}
            </span>
          )}
        </div>

        <div className="liqmap-page__sections">
          <ChartSection
            titleKey="liqMap.section_hyperliquid_title"
            titleFallback="Hyperliquid (on-chain)"
            subtitleKey="liqMap.section_hyperliquid_subtitle"
            subtitleFallback="Real positions tracked on Hyperliquid L1."
            symbol={symbol}
            hookResult={hlResult}
            testId="liqmap-section-hyperliquid"
            variant="real"
          />
          <ChartSection
            titleKey="liqMap.section_synthetic_title"
            titleFallback="Synthetic (OI model)"
            subtitleKey="liqMap.section_synthetic_subtitle"
            subtitleFallback="Modelled liquidation density from Binance / Bybit / OKX / BitMEX open interest."
            symbol={symbol}
            hookResult={synthResult}
            testId="liqmap-section-synthetic"
            variant="synthetic"
          />
          <ChartSection
            titleKey="liqMap.section_combined_title"
            titleFallback="Combined (all sources)"
            subtitleKey="liqMap.section_combined_subtitle"
            subtitleFallback="Sum of Hyperliquid + dYdX + synthetic, per-bucket."
            symbol={symbol}
            hookResult={combinedResult}
            testId="liqmap-section-combined"
            variant="combined"
          />
        </div>
      </div>
    </div>
  );
}

export default LiquidationMapPage;
export { SYMBOLS, STORAGE_KEY, prepareChartSeries };
